// Services Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Save, X, ChevronUp, ChevronDown, ShoppingCart, Users, Award, Wrench, Settings, MapPin, Truck, Phone, Mail, Clock, Shield, Cog } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  ShoppingCart, Users, Award, Wrench, Settings, MapPin, 
  Truck, Phone, Mail, Clock, Shield, Cog,
  Tool: Cog
};

// Available icons for services
const ICON_OPTIONS = [
  { value: 'ShoppingCart', label: 'Panier (Ventes)' },
  { value: 'Users', label: 'Utilisateurs (Support)' },
  { value: 'Award', label: 'Badge (Formation)' },
  { value: 'Wrench', label: 'Clé (Maintenance)' },
  { value: 'Settings', label: 'Engrenage (Assemblage)' },
  { value: 'MapPin', label: 'Position (SAV)' },
  { value: 'Truck', label: 'Camion (Livraison)' },
  { value: 'Phone', label: 'Téléphone' },
  { value: 'Mail', label: 'Email' },
  { value: 'Clock', label: 'Horloge' },
  { value: 'Shield', label: 'Bouclier (Garantie)' },
  { value: 'Cog', label: 'Engrenage 2' },
];

const ServiceForm = ({ service, onSave, onCancel, isNew }) => {
  const [form, setForm] = useState(service || {
    title_en: '', title_fr: '', title_ar: '',
    description_en: '', description_fr: '', description_ar: '',
    icon: 'Settings',
    order: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const IconPreview = ICON_MAP[form.icon] || Settings;

  return (
    <div className="bg-slate-50 rounded-xl p-6 space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium text-slate-800">
          {isNew ? 'Nouveau service' : 'Modifier le service'}
        </h4>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Icon Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Icône</label>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center">
            <IconPreview className="w-6 h-6 text-slate-600" />
          </div>
          <select
            value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
          >
            {ICON_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Titles */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Titre (FR)</label>
          <input
            type="text"
            value={form.title_fr}
            onChange={(e) => setForm({ ...form, title_fr: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="Titre en français"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Titre (EN)</label>
          <input
            type="text"
            value={form.title_en}
            onChange={(e) => setForm({ ...form, title_en: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="Title in English"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Titre (AR)</label>
          <input
            type="text"
            value={form.title_ar}
            onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            dir="rtl"
            placeholder="العنوان بالعربية"
          />
        </div>
      </div>

      {/* Descriptions */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description (FR)</label>
          <textarea
            value={form.description_fr}
            onChange={(e) => setForm({ ...form, description_fr: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            rows={3}
            placeholder="Description en français"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description (EN)</label>
          <textarea
            value={form.description_en}
            onChange={(e) => setForm({ ...form, description_en: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            rows={3}
            placeholder="Description in English"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description (AR)</label>
          <textarea
            value={form.description_ar}
            onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            rows={3}
            dir="rtl"
            placeholder="الوصف بالعربية"
          />
        </div>
      </div>

      {/* Active */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-sm text-slate-700">Actif</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/services`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSave = async (service) => {
    const isNew = !service.id;
    const url = isNew 
      ? `${BACKEND_URL}/api/admin/services`
      : `${BACKEND_URL}/api/admin/services/${service.id}`;
    
    try {
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(service)
      });
      
      if (response.ok) {
        fetchServices();
        setEditingId(null);
        setShowNew(false);
      }
    } catch (err) { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/services/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchServices();
    } catch (err) { /* silent */ }
  };

  const handleReorder = async (index, direction) => {
    const newServices = [...services];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newServices.length) return;
    
    // Swap orders
    const currentOrder = newServices[index].order;
    const targetOrder = newServices[targetIndex].order;
    
    newServices[index].order = targetOrder;
    newServices[targetIndex].order = currentOrder;
    
    // Swap positions in array
    [newServices[index], newServices[targetIndex]] = [newServices[targetIndex], newServices[index]];
    
    setServices(newServices);
    
    // Update both services in backend
    try {
      await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/services/${newServices[index].id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newServices[index])
        }),
        fetch(`${BACKEND_URL}/api/admin/services/${newServices[targetIndex].id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newServices[targetIndex])
        })
      ]);
    } catch (err) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Button */}
      {!showNew && !editingId && (
        <Button onClick={() => setShowNew(true)} className="mb-4">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un service
        </Button>
      )}

      {/* New Service Form */}
      {showNew && (
        <ServiceForm
          isNew
          service={{ order: services.length }}
          onSave={handleSave}
          onCancel={() => setShowNew(false)}
        />
      )}

      {/* Services List */}
      <div className="space-y-3">
        {services.map((service, index) => {
          const IconComponent = ICON_MAP[service.icon] || Settings;
          
          return editingId === service.id ? (
            <ServiceForm
              key={service.id}
              service={service}
              onSave={handleSave}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div 
              key={service.id} 
              className={`flex items-center gap-4 p-4 bg-white rounded-xl border ${service.is_active ? 'border-slate-200' : 'border-orange-200 bg-orange-50'}`}
            >
              {/* Reorder buttons */}
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleReorder(index, 'up')}
                  disabled={index === 0}
                  className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleReorder(index, 'down')}
                  disabled={index === services.length - 1}
                  className={`p-1 rounded ${index === services.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              {/* Icon */}
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <IconComponent className="w-6 h-6 text-slate-600" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-800">{service.title_fr || service.title_en}</h4>
                <p className="text-sm text-slate-500 truncate">{service.description_fr || service.description_en}</p>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                {!service.is_active && (
                  <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">Inactif</span>
                )}
                <button 
                  onClick={() => setEditingId(service.id)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <Edit className="w-4 h-4 text-slate-500" />
                </button>
                <button 
                  onClick={() => handleDelete(service.id)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {services.length === 0 && !showNew && (
        <div className="text-center py-12 text-slate-500">
          Aucun service. Cliquez sur "Ajouter un service" pour commencer.
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
